
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useSecureAdminAuth } from "@/hooks/useSecureAdminAuth";
import { Shield, Lock, User } from "lucide-react";

const AdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading } = useSecureAdminAuth();

  // Check if already logged in as admin
  useEffect(() => {
    if (isAuthenticated) {
      console.log("Admin already logged in, redirecting to dashboard");
      navigate('/admin-dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log("Login attempt:", { username, passwordLength: password.length });

    const success = await login(username, password);
    
    if (success) {
      console.log("Login successful, navigating to admin dashboard...");
      // Small delay to ensure state is updated
      setTimeout(() => {
        navigate('/admin-dashboard', { replace: true });
      }, 100);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-blue-50 to-cyan-50 p-4">
      <Card className="w-full max-w-md shadow-xl border-2 border-sky-100">
        <CardHeader className="text-center bg-gradient-to-r from-sky-50 to-blue-50">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="bg-gradient-to-r from-sky-500 to-blue-500 p-3 rounded-full">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-3xl font-fredoka bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent">
              TinyMinds Admin
            </CardTitle>
          </div>
          <p className="text-gray-600 text-lg">Sign in to access the admin panel</p>
          <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg text-sm border-2 border-amber-200">
            <p className="font-bold text-amber-800 text-base mb-2">🔐 Secure Admin Access</p>
            <p className="text-amber-700 font-medium">Please contact your system administrator for login credentials.</p>
            <p className="text-amber-600 text-xs mt-2">All login attempts are logged and monitored for security.</p>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="flex items-center gap-2 text-base font-medium">
                <User className="w-4 h-4 text-sky-600" />
                Username
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="h-12 border-2 border-sky-200 focus:border-sky-400 rounded-lg"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2 text-base font-medium">
                <Lock className="w-4 h-4 text-sky-600" />
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="h-12 border-2 border-sky-200 focus:border-sky-400 rounded-lg"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-600 hover:to-blue-600 text-white font-semibold rounded-lg text-base transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Signing In...
                </div>
              ) : (
                "🚀 Sign In"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
